/**
 * @param {number} degrees - Angle in degrees
 * @returns {number} Angle in radians
 */
function toRadians(degrees) {
  return degrees * (Math.PI / 180);
}

/**
 * Haversine distance between two lat/lng points in kilometres.
 * @param {number} lat1 - Origin latitude
 * @param {number} lon1 - Origin longitude
 * @param {number} lat2 - Destination latitude
 * @param {number} lon2 - Destination longitude
 * @returns {number} Distance in kilometres
 */
function calculateDistance(lat1, lon1, lat2, lon2) {
  const earthRadiusKm = 6371;

  const deltaLatitude = toRadians(lat2 - lat1);
  const deltaLongitude = toRadians(lon2 - lon1);
  const lat1Rad = toRadians(lat1);
  const lat2Rad = toRadians(lat2);

  const haversineValue =
    Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) +
    Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLongitude / 2) * Math.sin(deltaLongitude / 2);

  const centralAngleRadians = 2 * Math.atan2(Math.sqrt(haversineValue), Math.sqrt(1 - haversineValue));

  return earthRadiusKm * centralAngleRadians;
}

/**
 * @typedef {{ name: string, store_id: string, store_lat: string, store_lng: string, distanceKm?: number }} StoreData
 * @typedef {'available' | 'limited' | 'unavailable'} AvailabilityStatus
 * @typedef {Record<string, number>} StoreInventoryMap
 * @typedef {Record<string, StoreInventoryMap | number | string | null>} VariantInventoryMap
 */

/** Quantity thresholds: 0 = unavailable, 1 = limited, 2+ = available */
const LIMITED_MAX_QUANTITY = 1;

/**
 * Parses latitude/longitude from JSON or metaobject values (handles unicode minus signs).
 * @param {unknown} value - Raw coordinate value
 * @returns {number} Parsed coordinate, or NaN when invalid
 */
function parseCoordinate(value) {
  if (value == null || value === '') return Number.NaN;

  const normalized = String(value)
    .trim()
    .replace(/\u2212/g, '-');
  const parsed = Number.parseFloat(normalized);

  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

/**
 * @param {unknown} storeId - Store identifier from embedded JSON
 * @returns {string} Value for matching data-store-id attributes
 */
function getStoreIdAttributeValue(storeId) {
  if (storeId == null || storeId === '') return '';
  return String(storeId);
}

/**
 * @param {number} quantity - Inventory quantity for a store
 * @returns {AvailabilityStatus} Derived availability status
 */
function getAvailabilityStatus(quantity) {
  if (!Number.isFinite(quantity) || quantity <= 0) return 'unavailable';
  if (quantity <= LIMITED_MAX_QUANTITY) return 'limited';
  return 'available';
}

/**
 * @param {HTMLElement} statusEl - Status label element on a store card
 */
function removeStatusClasses(statusEl) {
  statusEl.classList.remove(
    'store-availability--status--available',
    'store-availability--status--limited',
    'store-availability--status--unavailable',
  );
}

/**
 * Hides the status until a variant is selected.
 * @param {HTMLElement} statusEl - Status label element on a store card
 */
function clearStatus(statusEl) {
  statusEl.hidden = true;
  statusEl.textContent = '';
  removeStatusClasses(statusEl);
  delete statusEl.dataset.status;
}

if (!customElements.get('store-availability')) {
  customElements.define(
    'store-availability',
    class StoreAvailability extends HTMLElement {
      /** @type {HTMLInputElement | null} */
      #checkbox = null;

      /** @type {HTMLElement | null} */
      #results = null;

      /** @type {HTMLElement | null} */
      #header = null;

      /** @type {HTMLElement | null} */
      #storesContainer = null;

      /** @type {HTMLElement | null} */
      #liveRegion = null;

      /** @type {StoreData[]} */
      #initialStores = [];

      /** @type {StoreData[]} */
      #stores = [];

      /** @type {{ latitude: number, longitude: number } | null} */
      #userPosition = null;

      /** @type {HTMLElement | null} */
      #variants = null;

      /** @type {boolean} */
      #syncingVariant = false;

      /** @type {VariantInventoryMap} */
      #variantInventory = {};

      /** @type {boolean} */
      #pickupInventory = false;

      /** @type {Set<string>} */
      #fetchedPickupVariants = new Set();

      /** @type {Map<string, Promise<void>>} */
      #pickupInventoryFetches = new Map();

      /** @type {number} */
      #statusPaintGeneration = 0;

      /** @type {string | null} */
      #trapId = null;

      /** @type {Record<AvailabilityStatus, string>} */
      #statusLabels = {
        available: 'Available',
        limited: 'Limited availability',
        unavailable: 'Not available',
      };

      /** @type {Record<string, string>} */
      #a11yLabels = {
        locationLoading: 'Finding your location',
        locationUnavailable: 'Location could not be determined. Showing stores in default order.',
        storesSorted: 'Stores sorted by distance from you',
        storesDefaultOrder: 'Stores shown in default order',
      };

      connectedCallback() {
        this.#checkbox = this.querySelector('#product_store_availability_checkbox');
        this.#results = this.querySelector('.product-store-availability--results');
        this.#header = this.querySelector('.product-store-availability--header');
        this.#storesContainer = this.querySelector('[data-store-availability-stores]');
        this.#liveRegion = this.querySelector('[data-store-availability-live-region]');
        this.#variants = this.querySelector('.product-store-availability--variants');
        this.#trapId = this.closest('[data-focus-trap]')?.getAttribute('data-focus-trap') || null;
        this.#initialStores = this.#parseStoresData();
        this.#stores = [...this.#initialStores];
        this.#variantInventory = this.#parseVariantInventoryData();
        this.#pickupInventory = this.dataset.pickupInventory === 'true';
        this.#statusLabels = {
          available: this.dataset.labelAvailable || this.#statusLabels.available,
          limited: this.dataset.labelLimited || this.#statusLabels.limited,
          unavailable: this.dataset.labelUnavailable || this.#statusLabels.unavailable,
        };
        this.#a11yLabels = {
          locationLoading: this.dataset.labelLocationLoading || this.#a11yLabels.locationLoading,
          locationUnavailable: this.dataset.labelLocationUnavailable || this.#a11yLabels.locationUnavailable,
          storesSorted: this.dataset.labelStoresSorted || this.#a11yLabels.storesSorted,
          storesDefaultOrder: this.dataset.labelStoresDefaultOrder || this.#a11yLabels.storesDefaultOrder,
        };

        this.#resetCheckboxState();
        this.#prefetchPickupInventories();
        this.#syncFromProductForm();
        this.#updateStoreStatusesFromSelectedVariant();
        this.#checkbox?.addEventListener('change', this.#onCheckboxChange);
        this.#variants?.addEventListener('change', this.#onVariantChange);
        document.addEventListener('product:variant-changed', this.#onProductVariantChanged);
        window.addEventListener('modal-trap-activate', this.#onModalActivate);
        window.addEventListener('pageshow', this.#onPageShow);
      }

      disconnectedCallback() {
        this.#checkbox?.removeEventListener('change', this.#onCheckboxChange);
        this.#variants?.removeEventListener('change', this.#onVariantChange);
        document.removeEventListener('product:variant-changed', this.#onProductVariantChanged);
        window.removeEventListener('modal-trap-activate', this.#onModalActivate);
        window.removeEventListener('pageshow', this.#onPageShow);
      }

      /**
       * @param {import('./types').ModalTrapActivateEvent} e - modal-trap-activate event
       */
      #onModalActivate = (e) => {
        if (!this.#trapId || e.detail?.id !== this.#trapId) return;

        this.#syncFromProductForm();
      };

      #onPageShow = () => {
        this.#setLocating(false);
        this.#resetCheckboxState();
        this.#updateStoreStatusesFromSelectedVariant();
      };

      /**
       * @param {Event} e - Variant radio change event
       */
      #onVariantChange = (e) => {
        const target = e.target;
        if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

        const variantId = target.dataset.optionVariantId;
        if (!variantId) return;

        this.#updateStoreStatuses(variantId);

        if (this.#syncingVariant) return;

        const productInput = document.querySelector(
          `#product-options input[type="radio"][data-option-variant-id="${CSS.escape(variantId)}"]`,
        );

        if (!(productInput instanceof HTMLInputElement) || productInput.checked) return;

        this.#syncingVariant = true;
        productInput.checked = true;
        productInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.#syncingVariant = false;
      };

      /**
       * @param {Event} e - product:variant-changed custom event
       */
      #onProductVariantChanged = (e) => {
        if (this.#syncingVariant) return;
        if (!(e instanceof CustomEvent)) return;

        const variantId = e.detail?.variantId;
        if (!variantId) return;

        this.#selectVariant(String(variantId));
      };

      #syncFromProductForm() {
        const variantId = this.#getSelectedVariantId();

        if (variantId) this.#selectVariant(variantId);
      }

      /**
       * @param {string} variantId - Variant ID to select in the tray
       */
      #selectVariant(variantId) {
        const input = this.#variants?.querySelector(
          `input[type="radio"][data-option-variant-id="${CSS.escape(variantId)}"]`,
        );

        if (input instanceof HTMLInputElement && !input.checked) {
          this.#syncingVariant = true;
          input.checked = true;
          this.#syncingVariant = false;
        }

        this.#updateStoreStatuses(variantId);
      }

      /**
       * @returns {string | null} Variant ID from tray radios, product form, or one-size default
       */
      #getSelectedVariantId() {
        const trayInput = this.#variants?.querySelector('input[type="radio"]:checked');

        if (trayInput instanceof HTMLInputElement && trayInput.dataset.optionVariantId) {
          return trayInput.dataset.optionVariantId;
        }

        const productInput = document.querySelector('#product-options input[type="radio"]:checked');

        if (productInput instanceof HTMLInputElement && productInput.dataset.optionVariantId) {
          return productInput.dataset.optionVariantId;
        }

        // One-size (00) / no size option: no radios — use the sellable variant from Liquid
        const defaultVariantId = this.dataset.defaultVariantId?.trim();

        return defaultVariantId || null;
      }

      #updateStoreStatusesFromSelectedVariant() {
        this.#updateStoreStatuses(this.#getSelectedVariantId());
      }

      /**
       * @param {string | null} variantId - Selected variant ID, or null when none
       */
      #updateStoreStatuses(variantId) {
        this.#setStoresVisible(Boolean(variantId));

        if (!this.#results) return;

        if (!variantId) {
          this.#paintStoreStatuses(null);
          return;
        }

        const id = String(variantId);

        if (!this.#needsPickupInventoryFetch(id)) {
          this.#paintStoreStatuses(id);
          return;
        }

        const generation = ++this.#statusPaintGeneration;
        this.#paintStoreStatuses(null);

        this.#ensurePickupInventory(id).then(() => {
          if (generation !== this.#statusPaintGeneration) return;
          if (this.#getSelectedVariantId() !== id) return;
          if (this.#needsPickupInventoryFetch(id)) return;

          this.#paintStoreStatuses(id);
        });
      }

      /**
       * @param {string | null} variantId - Selected variant ID, or null when none
       */
      #paintStoreStatuses(variantId) {
        if (!this.#results) return;

        const rawInventory = variantId ? this.#variantInventory[String(variantId)] : null;

        for (const card of this.#results.querySelectorAll('.store-availability-card')) {
          if (!(card instanceof HTMLElement)) continue;

          const storeId = card.dataset.storeId?.trim();
          const statusEl = card.querySelector('[data-availability-status]');
          if (!(statusEl instanceof HTMLElement)) continue;

          if (!variantId) {
            clearStatus(statusEl);
            continue;
          }

          /** @type {AvailabilityStatus} */
          let status = 'unavailable';

          const quantity = this.#getVariantStoreQuantity(rawInventory, storeId);

          if (Number.isFinite(quantity)) {
            status = getAvailabilityStatus(quantity);
          }

          this.#applyStatus(statusEl, status);
        }
      }

      /**
       * @param {string} variantId - Variant ID to inspect
       * @returns {boolean} Whether pickup inventory still needs to be fetched
       */
      #needsPickupInventoryFetch(variantId) {
        if (!this.#pickupInventory) return false;
        if (this.#fetchedPickupVariants.has(variantId)) return false;

        if (this.#hasLocationInventory(this.#variantInventory[variantId])) {
          this.#fetchedPickupVariants.add(variantId);
          return false;
        }

        return true;
      }

      /**
       * @param {StoreInventoryMap | number | string | null | undefined} rawInventory - Variant inventory payload
       * @returns {boolean} Whether the payload has per-location quantities
       */
      #hasLocationInventory(rawInventory) {
        const inventory = this.#normalizeInventory(typeof rawInventory === 'number' ? null : rawInventory);

        return Boolean(inventory && Object.keys(inventory).length > 0);
      }

      #prefetchPickupInventories() {
        if (!this.#pickupInventory) return;

        const variantIds = new Set();

        if (this.#variants) {
          for (const input of this.#variants.querySelectorAll('input[type="radio"][data-option-variant-id]')) {
            if (!(input instanceof HTMLInputElement)) continue;

            const variantId = input.dataset.optionVariantId?.trim();
            if (variantId) variantIds.add(variantId);
          }
        }

        const defaultVariantId = this.dataset.defaultVariantId?.trim();
        if (defaultVariantId) variantIds.add(defaultVariantId);

        for (const variantId of variantIds) {
          if (this.#needsPickupInventoryFetch(variantId)) {
            this.#ensurePickupInventory(variantId);
          }
        }
      }

      /**
       * @param {string} variantId - Variant ID to load pickup inventory for
       * @returns {Promise<void>}
       */
      #ensurePickupInventory(variantId) {
        if (!this.#needsPickupInventoryFetch(variantId)) return Promise.resolve();

        const inFlight = this.#pickupInventoryFetches.get(variantId);
        if (inFlight) return inFlight;

        const request = this.#fetchPickupInventory(variantId).finally(() => {
          this.#pickupInventoryFetches.delete(variantId);
        });

        this.#pickupInventoryFetches.set(variantId, request);

        return request;
      }

      /**
       * @param {string} variantId - Variant ID to load pickup inventory for
       * @returns {Promise<void>}
       */
      async #fetchPickupInventory(variantId) {
        const root = window.Shopify?.routes?.root || '/';

        try {
          const response = await fetch(
            `${root}variants/${encodeURIComponent(variantId)}/?section_id=section-ajax-store-availability-inventory`,
          );

          if (!response.ok) return;

          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const jsonEl = doc.querySelector('[data-store-availability-inventory]');

          if (!jsonEl?.textContent) return;

          const payload = JSON.parse(jsonEl.textContent);

          if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return;
          if (!Object.prototype.hasOwnProperty.call(payload, variantId)) return;

          this.#variantInventory = {
            ...this.#variantInventory,
            ...payload,
          };
          this.#fetchedPickupVariants.add(variantId);
        } catch {
          return;
        }
      }

      /**
       * @param {boolean} visible - Whether a variant is selected
       */
      #setStoresVisible(visible) {
        if (this.#storesContainer) this.#storesContainer.hidden = !visible;
        if (this.#header) this.#header.hidden = !visible;
      }

      /**
       * @param {StoreInventoryMap | number | string | null | undefined} rawInventory - Variant inventory payload
       * @param {string | undefined} storeId - Store identifier to look up
       * @returns {number} Quantity for the store, or NaN when missing
       */
      #getVariantStoreQuantity(rawInventory, storeId) {
        if (rawInventory == null || rawInventory === '') return Number.NaN;

        let parsed = rawInventory;

        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            return Number.NaN;
          }
        }

        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            return Number.NaN;
          }
        }

        // A bare number is aggregate inventory across locations - never apply it to every store card
        if (typeof parsed === 'number') return Number.NaN;

        const inventory = this.#normalizeInventory(parsed);
        if (!inventory || !storeId) return Number.NaN;

        return this.#getStoreQuantity(inventory, storeId);
      }

      /**
       * @param {StoreInventoryMap} inventory - Store quantity map for a variant
       * @param {string} storeId - Store identifier to look up
       * @returns {number} Quantity for the store, or NaN when missing
       */
      #getStoreQuantity(inventory, storeId) {
        if (Object.prototype.hasOwnProperty.call(inventory, storeId)) {
          return Number(inventory[storeId]);
        }

        const matchedKey = Object.keys(inventory).find((key) => key.trim() === storeId);
        return matchedKey ? Number(inventory[matchedKey]) : Number.NaN;
      }

      /**
       * @param {StoreInventoryMap | string | null | undefined} inventory - Raw inventory payload
       * @returns {StoreInventoryMap | null} Parsed inventory map, or null when invalid
       */
      #normalizeInventory(inventory) {
        if (inventory == null || inventory === '') return null;

        let parsed = inventory;

        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            return null;
          }
        }

        // Handle double-encoded JSON strings from text metafields
        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            return null;
          }
        }

        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;

        return /** @type {StoreInventoryMap} */ (parsed);
      }

      /**
       * @param {HTMLElement} statusEl - Status label element on a store card
       * @param {AvailabilityStatus} status - Availability status to apply
       */
      #applyStatus(statusEl, status) {
        statusEl.textContent = this.#statusLabels[status];
        removeStatusClasses(statusEl);
        statusEl.classList.add(`store-availability--status--${status}`);
        statusEl.dataset.status = status;
        statusEl.hidden = false;
      }

      /**
       * @param {string} message - Announcement for assistive technology
       */
      #announce(message) {
        if (!this.#liveRegion) return;

        // Clear first so repeated identical messages are still announced
        this.#liveRegion.textContent = '';
        requestAnimationFrame(() => {
          if (this.#liveRegion) this.#liveRegion.textContent = message;
        });
      }

      /**
       * @param {boolean} locating - Whether a geolocation request is in progress
       */
      #setLocating(locating) {
        if (!this.#checkbox) return;

        if (locating) {
          this.#checkbox.setAttribute('aria-busy', 'true');
          return;
        }

        this.#checkbox.removeAttribute('aria-busy');
      }

      #resetCheckboxState() {
        if (!this.#checkbox) return;

        this.#checkbox.checked = false;
        this.#restoreInitialOrder({ announce: false });
      }

      /**
       * @returns {StoreData[]} Parsed store list from embedded JSON
       */
      #parseStoresData() {
        const dataElement = this.querySelector('#jp-stores-data');

        if (!dataElement?.textContent) return [];

        try {
          return JSON.parse(dataElement.textContent);
        } catch {
          return [];
        }
      }

      /**
       * @returns {VariantInventoryMap} Parsed variant inventory map from embedded JSON
       */
      #parseVariantInventoryData() {
        const dataElement = this.querySelector('#jp-variant-inventory-data');

        if (!dataElement?.textContent) return {};

        try {
          return JSON.parse(dataElement.textContent);
        } catch {
          return {};
        }
      }

      #onCheckboxChange = () => {
        if (!this.#checkbox?.checked) {
          this.#setLocating(false);
          this.#restoreInitialOrder({ announce: true });
          return;
        }

        this.#applyNearestStoresOrder();
      };

      /**
       * @param {{ announce?: boolean }} [options] - Whether to announce the reset to AT
       */
      #restoreInitialOrder({ announce = false } = {}) {
        this.#stores = [...this.#initialStores];
        this.#reorderStoreCards();
        this.#clearDistanceLabels();

        if (announce) this.#announce(this.#a11yLabels.storesDefaultOrder);
      }

      #applyNearestStoresOrder() {
        if (this.#userPosition) {
          this.#stores = this.#sortStoresByDistance(this.#userPosition.latitude, this.#userPosition.longitude);
          this.#reorderStoreCards();
          this.#updateDistanceLabels();
          this.#announce(this.#a11yLabels.storesSorted);
          return;
        }

        this.#requestUserLocation();
      }

      #handleLocationUnavailable() {
        this.#setLocating(false);

        if (this.#checkbox) this.#checkbox.checked = false;

        this.#restoreInitialOrder({ announce: false });
        this.#announce(this.#a11yLabels.locationUnavailable);
      }

      #requestUserLocation() {
        if (!navigator.geolocation) {
          this.#handleLocationUnavailable();
          return;
        }

        this.#setLocating(true);
        this.#announce(this.#a11yLabels.locationLoading);

        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.#userPosition = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };

            this.#setLocating(false);

            if (!this.#checkbox?.checked) return;

            this.#stores = this.#sortStoresByDistance(this.#userPosition.latitude, this.#userPosition.longitude);
            this.#reorderStoreCards();
            this.#updateDistanceLabels();
            this.#announce(this.#a11yLabels.storesSorted);
          },
          () => {
            this.#handleLocationUnavailable();
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            // Allow the browser to reuse a recent cached position when available, default: 300000ms (5 minutes)
            maximumAge: 300000,
          },
        );
      }

      /**
       * @param {number} userLat - User latitude
       * @param {number} userLng - User longitude
       * @returns {StoreData[]} Stores sorted nearest-first
       */
      #sortStoresByDistance(userLat, userLng) {
        return [...this.#initialStores]
          .map((store) => {
            const storeLat = parseCoordinate(store.store_lat);
            const storeLng = parseCoordinate(store.store_lng);
            const distanceKm =
              Number.isFinite(storeLat) && Number.isFinite(storeLng)
                ? calculateDistance(userLat, userLng, storeLat, storeLng)
                : Number.POSITIVE_INFINITY;

            return {
              ...store,
              distanceKm,
            };
          })
          .sort((a, b) => (a.distanceKm ?? Number.POSITIVE_INFINITY) - (b.distanceKm ?? Number.POSITIVE_INFINITY));
      }

      /**
       * @param {unknown} storeId - Store identifier from embedded JSON
       * @returns {HTMLElement | null} Matching store card
       */
      #getStoreCard(storeId) {
        if (!this.#results) return null;

        return this.#results.querySelector(
          `.store-availability-card[data-store-id="${CSS.escape(getStoreIdAttributeValue(storeId))}"]`,
        );
      }

      #reorderStoreCards() {
        if (!this.#results) return;

        for (const store of this.#stores) {
          const card = this.#getStoreCard(store.store_id);

          if (card) {
            this.#results.appendChild(card);
          }
        }
      }

      /**
       * @param {number} distanceKm - Distance in kilometres
       * @returns {string} Localised distance label
       */
      #formatDistance(distanceKm) {
        const rounded = distanceKm.toFixed(1);
        const template = this.dataset.distanceTemplate || '{{ distance }}km';

        return template.replace('{{ distance }}', rounded);
      }

      #updateDistanceLabels() {
        if (!this.#results) return;

        for (const store of this.#stores) {
          const card = this.#getStoreCard(store.store_id);
          const distanceEl = card?.querySelector('.store-availability-card--distance');

          if (!(distanceEl instanceof HTMLElement)) continue;

          if (store.distanceKm == null || !Number.isFinite(store.distanceKm)) {
            distanceEl.textContent = '';
            distanceEl.setAttribute('aria-hidden', 'true');
            distanceEl.hidden = true;
            continue;
          }

          distanceEl.textContent = '(' + this.#formatDistance(store.distanceKm) + ')';
          distanceEl.removeAttribute('aria-hidden');
          distanceEl.hidden = false;
        }
      }

      #clearDistanceLabels() {
        if (!this.#results) return;

        for (const distanceEl of this.#results.querySelectorAll('.store-availability-card--distance')) {
          if (!(distanceEl instanceof HTMLElement)) continue;

          distanceEl.textContent = '';
          distanceEl.setAttribute('aria-hidden', 'true');
          distanceEl.hidden = true;
        }
      }
    },
  );
}
